/**
 * 右侧面板组件 - 参考 makeverse.cn 设计风格
 *
 * 提供 Chat / Workflow / AgentTeam 的 Tab 切换功能
 */

import React, { useState, ReactNode } from 'react';

// ==================== 类型定义 ====================

export type RightPanelTab = 'chat' | 'workflow' | 'agentteam';

interface RightPanelProps {
  /** Chat 面板内容 */
  chatPanel: ReactNode;
  /** Workflow 面板内容 */
  workflowPanel?: ReactNode;
  /** AgentTeam 面板内容 */
  agentteamPanel?: ReactNode;
  /** 默认激活的 Tab */
  defaultTab?: RightPanelTab;
  /** Tab 切换回调 */
  onTabChange?: (tab: RightPanelTab) => void;
}

// ==================== 功能开关 ====================

// 工作流/团队功能仍在开发中，暂时隐藏入口（置为 true 可恢复）
const FEATURE_FLAGS = {
  workflow: false,
  agentTeam: false,
};

// ==================== Tab 配置 ====================

const TAB_CONFIG: { key: RightPanelTab; label: string; icon: React.ReactNode }[] = [
  {
    key: 'chat',
    label: '聊天',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
  {
    key: 'workflow',
    label: '工作流',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-2V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    ),
  },
  {
    key: 'agentteam',
    label: '团队',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
];

// ==================== 主组件 ====================

/**
 * 右侧面板组件
 *
 * @param {RightPanelProps} props - 组件属性
 * @returns {JSX.Element} 右侧面板组件
 */
export const RightPanel: React.FC<RightPanelProps> = ({
  chatPanel,
  workflowPanel,
  agentteamPanel,
  defaultTab = 'chat',
  onTabChange,
}) => {
  const [activeTab, setActiveTab] = useState<RightPanelTab>(defaultTab);

  const handleTabClick = (tab: RightPanelTab) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  // 渲染当前 Tab 内容
  const renderTabContent = () => {
    switch (activeTab) {
      case 'chat':
        return chatPanel;
      case 'workflow':
        return workflowPanel || (
          <div className="h-full flex flex-col items-center justify-center bg-white p-8">
            <div className="text-center max-w-md">
              <div className="text-6xl mb-4">⚙️</div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">工作流</h2>
              <p className="text-gray-600">工作流功能正在加载中...</p>
            </div>
          </div>
        );
      case 'agentteam':
        return agentteamPanel || (
          <div className="h-full flex flex-col items-center justify-center bg-white p-8">
            <div className="text-center max-w-md">
              <div className="text-6xl mb-4">👥</div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">智能体团队</h2>
              <p className="text-gray-600">团队协作功能正在加载中...</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Tab 栏 */}
      <div className="flex items-center border-b border-gray-100 px-3 py-2 bg-gray-50 flex-shrink-0">
        {TAB_CONFIG.filter((tab) =>
          tab.key === 'workflow' ? FEATURE_FLAGS.workflow
          : tab.key === 'agentteam' ? FEATURE_FLAGS.agentTeam
          : true
        ).map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabClick(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-150 ${
              activeTab === tab.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            aria-label={tab.label}
            aria-pressed={activeTab === tab.key}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab 内容 */}
      <div className="flex-1 overflow-hidden">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default RightPanel;
